import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Search } from "lucide-react";

interface CanvasItem {
  uuid: string;
  title: string;
  isPublic: boolean;
  thumbnailUrl: string | null;
  createdAt: string;
  deletedAt: string | null;
  user: { id: string; name: string; email: string };
  project: { id: string; name: string } | null;
  nodeCount: number;
  pageCount: number;
}

interface CanvasStats {
  total: number;
  active: number;
  deleted: number;
  public: number;
  private: number;
  canvasesThisWeek: number;
  canvasesThisMonth: number;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const canvasSearchSchema = z.object({
  search: z.string().optional(),
  includeDeleted: z.string().optional(),
  isPublic: z.string().optional(),
  page: z.coerce.number().optional(),
})

export const Route = createFileRoute('/dashboard/canvas/')({
  validateSearch: canvasSearchSchema,
  component: CanvasListPage,
})

function CanvasListPage() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const [canvases, setCanvases] = React.useState<CanvasItem[]>([]);
  const [stats, setStats] = React.useState<CanvasStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [search, setSearch] = React.useState(searchParams.search || "");
  const [includeDeleted, setIncludeDeleted] = React.useState(searchParams.includeDeleted === "true");
  const [publicOnly, setPublicOnly] = React.useState(searchParams.isPublic === "true");
  const [page, setPage] = React.useState(searchParams.page || 1);
  const limit = 20;

  const fetchCanvases = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      if (search) params.set("search", search);
      if (includeDeleted) params.set("includeDeleted", "true");
      if (publicOnly) params.set("isPublic", "true");
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/canvas?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setCanvases(json.data.canvases);
      setTotal(json.data.total);
    } catch (e) {
      console.error("Fetch canvases error:", e);
    } finally {
      setLoading(false);
    }
  }, [search, includeDeleted, publicOnly, page]);

  React.useEffect(() => { fetchCanvases(); }, [fetchCanvases]);
  React.useEffect(() => {
    fetch(`${API_ADMIN_BASE_URL}/api/admin/canvas/stats`, { credentials: "include" })
      .then((r) => r.json()).then((j) => setStats(j.data)).catch(() => {});
  }, []);
  React.useEffect(() => { document.title = "Canvas - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [search, includeDeleted, publicOnly]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Canvas</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border flex flex-col border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Canvas</h1>
          <p className="text-muted-foreground text-sm">{total.toLocaleString()} canvases{stats ? ` \u00b7 ${stats.canvasesThisWeek} this week` : ""}</p>
          {stats && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Total</span><span className="text-lg font-semibold">{stats.total.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Active</span><span className="text-lg font-semibold">{stats.active.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Deleted</span><span className="text-lg font-semibold">{stats.deleted.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Public</span><span className="text-lg font-semibold">{stats.public.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">This Week</span><span className="text-lg font-semibold">{stats.canvasesThisWeek.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">This Month</span><span className="text-lg font-semibold">{stats.canvasesThisMonth.toLocaleString()}</span></div>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full md:w-[300px]">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input placeholder="Search title, slug, or email..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2"><Switch checked={publicOnly} onCheckedChange={setPublicOnly} id="public" /><Label htmlFor="public" className="text-sm">Public only</Label></div>
            <div className="flex items-center gap-2"><Switch checked={includeDeleted} onCheckedChange={setIncludeDeleted} id="deleted" /><Label htmlFor="deleted" className="text-sm">Include deleted</Label></div>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[250px]">Title</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead className="text-center">Nodes</TableHead>
                  <TableHead className="text-center">Pages</TableHead>
                  <TableHead>Public</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : canvases.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No canvases found</TableCell></TableRow>
                ) : canvases.map((c) => (
                  <TableRow key={c.uuid} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate({ to: `/dashboard/canvas/${c.uuid}` })}>
                    <TableCell className="font-medium truncate max-w-[250px]">{c.title}</TableCell>
                    <TableCell><span className="text-sm">{c.user.email}</span></TableCell>
                    <TableCell><span className="text-sm text-muted-foreground">{c.project?.name || "-"}</span></TableCell>
                    <TableCell className="text-center">{c.nodeCount}</TableCell>
                    <TableCell className="text-center">{c.pageCount}</TableCell>
                    <TableCell>{c.isPublic ? <Badge variant="default" className="text-xs">Public</Badge> : <span className="text-muted-foreground text-xs">Private</span>}</TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">{formatDate(c.createdAt)}</span></TableCell>
                    <TableCell>{c.deletedAt ? <Badge variant="destructive" className="text-xs">Deleted</Badge> : <Badge variant="outline" className="text-xs text-green-600">Active</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(1, page - 1))} className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pn: number;
                if (totalPages <= 5) pn = i + 1;
                else if (page <= 3) pn = i + 1;
                else if (page >= totalPages - 2) pn = totalPages - 4 + i;
                else pn = page - 2 + i;
                return (<PaginationItem key={pn}><PaginationLink onClick={() => setPage(pn)} isActive={page === pn} className="cursor-pointer">{pn}</PaginationLink></PaginationItem>);
              })}
              <PaginationItem><PaginationNext onClick={() => setPage(Math.min(totalPages, page + 1))} className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>
    </div>
  );
}
